import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CANNED_RESPONSE_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { hasPermissions } from 'dashboard/helper/permissionsHelper';

/*
 * Floating selection menu of the reply composer (ProseMirror menubar).
 *
 * The menubar is rendered with `position: fixed` and viewport coordinates so
 * it is never clipped by the `overflow-hidden` wrappers around the composer.
 * The helpers below are pure so the placement rules can be unit tested.
 */

export const SELECTION_MENU_GAP = 8;
export const SELECTION_MENU_MARGIN = 8;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Restricts the selection box to the visible area of the editor body, so the
 * menu follows the visible part of a selection that was scrolled away.
 */
export const clipSelectionRect = (rect, clip) => {
  if (!clip) return rect;
  const top = clamp(rect.top, clip.top, clip.bottom);
  const bottom = clamp(rect.bottom, clip.top, clip.bottom);
  const left = clamp(rect.left, clip.left, clip.right);
  const right = clamp(rect.right, clip.left, clip.right);
  return { top, bottom: Math.max(top, bottom), left, right };
};

/**
 * Computes the viewport position of a floating menu anchored to a selection.
 * Prefers the space above the selection, flips below when it does not fit and
 * shifts horizontally so the menu never leaves the viewport.
 *
 * @param {Object} params
 * @param {{top:number,bottom:number,left:number,right:number}} params.anchor
 *   Selection box in viewport coordinates.
 * @param {{width:number,height:number}} params.menu Rendered menu size.
 * @param {{width:number,height:number}} params.viewport Viewport size.
 * @param {{top:number,bottom:number,left:number,right:number}} [params.clip]
 *   Visible area of the editor (the anchor is clamped to it).
 * @param {boolean} [params.isRtl]
 * @param {number} [params.gap] Distance between selection and menu.
 * @param {number} [params.margin] Minimum distance to the viewport edges.
 * @returns {{top:number,left:number,placement:'top'|'bottom'}}
 */
export const computeSelectionMenuPosition = ({
  anchor,
  menu,
  viewport,
  clip = null,
  isRtl = false,
  gap = SELECTION_MENU_GAP,
  margin = SELECTION_MENU_MARGIN,
}) => {
  const box = clipSelectionRect(anchor, clip);
  const needed = menu.height + gap;
  const spaceAbove = box.top - margin;
  const spaceBelow = viewport.height - box.bottom - margin;

  let placement = 'top';
  if (spaceAbove < needed) {
    placement =
      spaceBelow >= needed || spaceBelow > spaceAbove ? 'bottom' : 'top';
  }

  const rawTop =
    placement === 'top' ? box.top - gap - menu.height : box.bottom + gap;
  const maxTop = Math.max(margin, viewport.height - menu.height - margin);
  const top = clamp(rawTop, margin, maxTop);

  const rawLeft = isRtl ? box.right - menu.width : box.left;
  const maxLeft = Math.max(margin, viewport.width - menu.width - margin);
  const left = clamp(rawLeft, margin, maxLeft);

  return { top: Math.round(top), left: Math.round(left), placement };
};

/**
 * Quick reply (canned response) shortcut of the selection menu.
 * Same access rule as the quick replies settings page.
 */
export const QUICK_REPLY_REQUIRED_PERMISSIONS = [
  ...ROLES,
  ...CONVERSATION_PERMISSIONS,
  CANNED_RESPONSE_PERMISSIONS,
];

export const canCreateQuickReply = (permissions = []) =>
  hasPermissions(QUICK_REPLY_REQUIRED_PERMISSIONS, permissions);

const SHORT_CODE_MAX_WORDS = 3;
const SHORT_CODE_MAX_LENGTH = 30;

/**
 * Suggests an editable short code from the first words of the text:
 * lowercase, without accents, words joined by "_".
 */
export const suggestQuickReplyShortCode = (text = '') => {
  const words = String(text)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, SHORT_CODE_MAX_WORDS);

  return words.join('_').slice(0, SHORT_CODE_MAX_LENGTH).replace(/_+$/, '');
};

export const QUICK_REPLY_MENU_ITEM_CLASS = 'ProseMirror-quick-reply';
export const QUICK_REPLY_ICON_CLASS = 'i-hugeicons-message-add-01';

/**
 * Adds (or removes) the "Criar resposta rápida" button in the ProseMirror
 * menubar. The menubar DOM is owned by prosemirror-menu and recreated when the
 * editor state is reloaded, so this is called whenever the menu is shown.
 *
 * @param {HTMLElement|null} menubar `.ProseMirror-menubar` element.
 * @param {{visible:boolean,label:string,onActivate:Function}} options
 * @returns {HTMLButtonElement|null} The button, when visible.
 */
export const syncQuickReplyMenuItem = (
  menubar,
  { visible = false, label = '', onActivate = () => {} } = {}
) => {
  if (!menubar) return null;
  const existing = menubar.querySelector(`.${QUICK_REPLY_MENU_ITEM_CLASS}`);

  if (!visible) {
    existing?.closest('.ProseMirror-menuitem')?.remove();
    return null;
  }

  if (existing) {
    existing.setAttribute('aria-label', label);
    existing.setAttribute('title', label);
    // eslint-disable-next-line no-underscore-dangle
    existing._onActivate = onActivate;
    return existing;
  }

  const doc = menubar.ownerDocument;
  const wrapper = doc.createElement('span');
  wrapper.className = 'ProseMirror-menuitem ProseMirror-menuitem--quick-reply';

  const button = doc.createElement('button');
  button.type = 'button';
  button.className = `ProseMirror-icon ${QUICK_REPLY_MENU_ITEM_CLASS}`;
  button.setAttribute('aria-label', label);
  button.setAttribute('title', label);
  // eslint-disable-next-line no-underscore-dangle
  button._onActivate = onActivate;

  const icon = doc.createElement('span');
  icon.className = QUICK_REPLY_ICON_CLASS;
  icon.setAttribute('aria-hidden', 'true');
  button.appendChild(icon);

  // Keep the editor focus and the text selection while clicking.
  button.addEventListener('mousedown', event => event.preventDefault());
  button.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();
    // eslint-disable-next-line no-underscore-dangle
    button._onActivate?.();
  });

  wrapper.appendChild(button);
  menubar.appendChild(wrapper);
  return button;
};
